<template>
  <HrPageLayout
    title="Appointment Management"
    subtitle="New appointments, promotions, confirmations and redesignations — with filtered CSV & PDF reports"
    icon="📋"
    :breadcrumb-items="[{ label: 'Home', route: '/dashboard' }, { label: 'Appointments' }]"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-outline-primary" :disabled="loading" @click="load">
        <i class="bx bx-refresh"></i> Refresh
      </button>
      <button type="button" class="hr-btn hr-btn-outline-success" :disabled="exporting || !rows.length" @click="exportCsv">
        <i class="bx bx-file"></i> CSV
      </button>
      <button type="button" class="hr-btn hr-btn-outline-success" :disabled="exporting || !rows.length" @click="exportPdf">
        <i class="bx bxs-file-pdf"></i> PDF
      </button>
      <button
        v-if="tab === 'new_appointments'"
        type="button"
        class="hr-btn hr-btn-primary"
        @click="openNew"
      >
        <i class="bx bx-plus"></i> New appointment
      </button>
    </template>

    <div class="ap">
      <div class="ap-tabs">
        <button
          v-for="t in tabs"
          :key="t.id"
          type="button"
          class="ap-tab"
          :class="{ active: tab === t.id }"
          @click="switchTab(t.id)"
        >{{ t.label }}</button>
      </div>

      <div class="ap-filters">
        <label>Search
          <input v-model="filters.search" type="text" placeholder="Name, designation…" @keyup.enter="load">
        </label>
        <label>From
          <input v-model="filters.date_from" type="date">
        </label>
        <label>To
          <input v-model="filters.date_to" type="date">
        </label>
        <label>Status
          <input v-model="filters.status" type="text" placeholder="Optional">
        </label>
        <label>Department
          <input v-model="filters.department_name" type="text" placeholder="Optional">
        </label>
        <button type="button" class="hr-btn hr-btn-primary" :disabled="loading" @click="load">Apply</button>
      </div>

      <div class="ap-meta">
        <span>{{ total }} record{{ total === 1 ? '' : 's' }}</span>
        <span class="muted">{{ currentTabLabel }} report</span>
      </div>

      <div v-if="loading" class="ap-muted pad">Loading…</div>
      <div v-else class="ap-card">
        <table class="ap-table">
          <thead>
            <tr>
              <th>#</th>
              <th v-for="h in displayHeaders" :key="h.key">{{ h.label }}</th>
              <th v-if="tab === 'new_appointments'"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, i) in rows" :key="r.id || i">
              <td>{{ i + 1 }}</td>
              <td v-for="h in displayHeaders" :key="h.key">{{ display(r[h.key]) }}</td>
              <td v-if="tab === 'new_appointments'" class="acts">
                <button type="button" class="hr-btn hr-btn-sm" @click="edit(r)">Edit</button>
              </td>
            </tr>
            <tr v-if="!rows.length">
              <td :colspan="displayHeaders.length + 2" class="ap-muted pad">No records for the selected filters.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="show" class="ap-ov" @click.self="show = false">
      <div class="ap-modal" @click.stop>
        <div class="ap-mh">
          <h3>{{ form.id ? 'Edit appointment' : 'New appointment' }}</h3>
          <button type="button" class="ap-x" @click="show = false">&times;</button>
        </div>
        <div class="ap-mb">
          <label>Candidate name * <input v-model="form.candidate_name"></label>
          <label>Emp No <input v-model="form.emp_no"></label>
          <label>Vacancy / post <input v-model="form.vacancy_title"></label>
          <label>Department <input v-model="form.department_name"></label>
          <label>Job group <input v-model="form.job_group"></label>
          <label>Appointment type <input v-model="form.appointment_type"></label>
          <label>Start date <input v-model="form.start_date" type="date"></label>
          <label>Status
            <select v-model="form.status">
              <option>Pending</option>
              <option>Offered</option>
              <option>Accepted</option>
              <option>Onboarded</option>
              <option>Declined</option>
              <option>Cancelled</option>
            </select>
          </label>
          <label class="full">Notes <textarea v-model="form.notes" rows="3"></textarea></label>
        </div>
        <div class="ap-ma">
          <button type="button" class="hr-btn hr-btn-secondary" @click="show = false">Cancel</button>
          <button type="button" class="hr-btn hr-btn-primary" :disabled="saving" @click="save">{{ saving ? 'Saving…' : 'Save' }}</button>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, toast } from "@/api";
import {
  downloadCsv,
  downloadCountyTablePdf,
  periodLabelFromFilters,
} from "@/utils/countyReportExport";

const TAB_HEADERS = {
  new_appointments: [
    { key: "employee_name", label: "Candidate" },
    { key: "emp_no", label: "Emp No" },
    { key: "vacancy", label: "Vacancy" },
    { key: "department_name", label: "Department" },
    { key: "appointment_type", label: "Type" },
    { key: "start_date", label: "Start" },
    { key: "status", label: "Status" },
  ],
  promotions: [
    { key: "employee_name", label: "Employee" },
    { key: "emp_no", label: "Emp No" },
    { key: "department_name", label: "Department" },
    { key: "from_designation", label: "From" },
    { key: "to_designation", label: "To" },
    { key: "promotion_type", label: "Type" },
    { key: "effective_date", label: "Effective" },
    { key: "status", label: "Status" },
  ],
  confirmations: [
    { key: "employee_name", label: "Employee" },
    { key: "emp_no", label: "Emp No" },
    { key: "department_name", label: "Department" },
    { key: "probation_end", label: "Probation end" },
    { key: "confirmation_date", label: "Confirmed" },
    { key: "status", label: "Status" },
  ],
  redesignations: [
    { key: "employee_name", label: "Employee" },
    { key: "current_title", label: "Current" },
    { key: "proposed_title", label: "Proposed" },
    { key: "status", label: "Status" },
    { key: "created_at", label: "Created" },
  ],
};

const emptyForm = () => ({
  id: null,
  candidate_name: "",
  emp_no: "",
  vacancy_title: "",
  department_name: "",
  job_group: "",
  appointment_type: "New Appointment",
  start_date: "",
  status: "Pending",
  notes: "",
});

export default {
  name: "AppointmentManagement",
  components: { HrPageLayout },
  data() {
    return {
      tab: "new_appointments",
      tabs: [
        { id: "new_appointments", label: "New Appointments" },
        { id: "promotions", label: "Promotions" },
        { id: "confirmations", label: "Confirmations" },
        { id: "redesignations", label: "Redesignations" },
      ],
      filters: { search: "", date_from: "", date_to: "", status: "", department_name: "" },
      rows: [],
      total: 0,
      loading: false,
      exporting: false,
      saving: false,
      show: false,
      form: emptyForm(),
    };
  },
  computed: {
    displayHeaders() {
      return TAB_HEADERS[this.tab] || TAB_HEADERS.new_appointments;
    },
    currentTabLabel() {
      return (this.tabs.find((t) => t.id === this.tab) || {}).label || "";
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    display(v) {
      if (v == null || v === "") return "—";
      return String(v);
    },
    switchTab(id) {
      this.tab = id;
      this.load();
    },
    buildFd(extra = {}) {
      const fd = new FormData();
      fd.append("function", "appointmentHubList");
      fd.append("report_type", this.tab);
      fd.append("search", this.filters.search || "");
      fd.append("date_from", this.filters.date_from || "");
      fd.append("date_to", this.filters.date_to || "");
      fd.append("status", this.filters.status || "");
      fd.append("department_name", this.filters.department_name || "");
      fd.append("limit", String(extra.limit || 200));
      fd.append("page", String(extra.page || 1));
      if (extra.exportAll) fd.append("exportAll", "1");
      return fd;
    },
    async load() {
      this.loading = true;
      try {
        const res = await execute(this.buildFd(), authUrl);
        if (res?.data?.success) {
          this.rows = res.data.data?.rows || [];
          this.total = Number(res.data.data?.total || this.rows.length);
        } else {
          this.rows = [];
          this.total = 0;
          toast.error(res?.data?.message || "Failed to load");
        }
      } catch (e) {
        this.rows = [];
        toast.error("Failed to load appointments data");
      } finally {
        this.loading = false;
      }
    },
    async fetchAll() {
      const res = await execute(this.buildFd({ exportAll: true, limit: 5000 }), authUrl);
      if (!res?.data?.success) throw new Error(res?.data?.message || "Export list failed");
      return res.data.data?.rows || [];
    },
    openNew() {
      this.form = emptyForm();
      this.show = true;
    },
    edit(r) {
      this.form = {
        id: r.id,
        candidate_name: r.employee_name || r.candidate || "",
        emp_no: r.emp_no || "",
        vacancy_title: r.vacancy || "",
        department_name: r.department_name || "",
        job_group: r.job_group || "",
        appointment_type: r.appointment_type || "New Appointment",
        start_date: (r.start_date || "").toString().slice(0, 10),
        status: r.status || "Pending",
        notes: r.notes || "",
      };
      this.show = true;
    },
    async save() {
      if (!this.form.candidate_name.trim()) {
        toast.error("Candidate name required");
        return;
      }
      this.saving = true;
      try {
        const fd = new FormData();
        fd.append("function", "saveAppointment");
        Object.keys(this.form).forEach((k) => {
          if (this.form[k] != null && this.form[k] !== "") fd.append(k, String(this.form[k]));
        });
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          toast.success("Saved");
          this.show = false;
          this.load();
        } else toast.error(res?.data?.message || "Save failed");
      } finally {
        this.saving = false;
      }
    },
    rowCells(r) {
      return this.displayHeaders.map((h) => r[h.key] ?? "");
    },
    async exportCsv() {
      this.exporting = true;
      try {
        const all = await this.fetchAll();
        downloadCsv(
          this.displayHeaders.map((h) => h.label),
          all.map((r) => this.rowCells(r)),
          `appointment_${this.tab}_${Date.now()}.csv`
        );
        toast.success(`CSV downloaded (${all.length} rows)`);
      } catch (e) {
        toast.error(e?.message || "CSV failed");
      } finally {
        this.exporting = false;
      }
    },
    async exportPdf() {
      this.exporting = true;
      try {
        const all = await this.fetchAll();
        await downloadCountyTablePdf({
          title: `${this.currentTabLabel} Report`.toUpperCase(),
          subtitle: "Appointment Management",
          periodLabel: periodLabelFromFilters(this.filters) || new Date().toLocaleString("en-KE"),
          headers: this.displayHeaders.map((h) => h.label),
          rows: all.map((r) => this.rowCells(r)),
          filename: `appointment_${this.tab}_${Date.now()}.pdf`,
          summaryRows: [
            ["Records", String(all.length)],
            ["Search", this.filters.search || "—"],
            ["Status filter", this.filters.status || "All"],
          ],
        });
        toast.success("PDF downloaded");
      } catch (e) {
        toast.error(e?.message || "PDF failed");
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<style scoped>
.ap { display: flex; flex-direction: column; gap: 1rem; }
.ap-tabs { display: flex; flex-wrap: wrap; gap: 0.4rem; }
.ap-tab {
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 999px;
  padding: 0.4rem 0.9rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
}
.ap-tab.active { background: #0c4a6e; color: #fff; border-color: #0c4a6e; }
.ap-filters {
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr 1fr 1fr auto;
  gap: 0.65rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 0.9rem 1rem;
  align-items: end;
}
.ap-filters label {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  font-size: 0.72rem;
  font-weight: 700;
  color: #64748b;
}
.ap-filters input {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.45rem 0.6rem;
  background: #f8fafc;
  font-weight: 500;
  color: #0f172a;
}
.ap-meta { display: flex; justify-content: space-between; font-size: 0.85rem; font-weight: 600; color: #334155; }
.muted { color: #94a3b8; font-weight: 500; }
.ap-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; overflow: auto; }
.ap-table { width: 100%; border-collapse: collapse; font-size: 0.86rem; }
.ap-table th, .ap-table td { padding: 0.65rem 0.75rem; border-bottom: 1px solid #f1f5f9; text-align: left; }
.ap-table th { background: #f8fafc; font-size: 0.72rem; text-transform: uppercase; color: #64748b; }
.ap-muted { color: #94a3b8; }
.pad { padding: 1.5rem; }
.acts { white-space: nowrap; }
.ap-ov {
  position: fixed; inset: 0; background: rgba(15,23,42,.5);
  display: flex; align-items: center; justify-content: center; z-index: 10050; padding: 1rem;
}
.ap-modal { background: #fff; width: min(560px, 96vw); border-radius: 14px; max-height: 92vh; overflow: auto; }
.ap-mh { display: flex; justify-content: space-between; padding: 1rem 1.15rem; border-bottom: 1px solid #e2e8f0; }
.ap-mh h3 { margin: 0; font-size: 1.05rem; }
.ap-x { border: none; background: none; font-size: 1.4rem; cursor: pointer; }
.ap-mb { padding: 1rem 1.15rem; display: grid; grid-template-columns: 1fr 1fr; gap: 0.7rem; }
.ap-mb label { display: flex; flex-direction: column; gap: 0.3rem; font-size: 0.75rem; font-weight: 700; color: #475569; }
.ap-mb label.full { grid-column: 1 / -1; }
.ap-mb input, .ap-mb select, .ap-mb textarea {
  border: 1px solid #e2e8f0; border-radius: 8px; padding: 0.5rem 0.65rem; background: #f8fafc;
}
.ap-ma { display: flex; justify-content: flex-end; gap: 0.5rem; padding: 0 1.15rem 1rem; }
@media (max-width: 900px) {
  .ap-filters { grid-template-columns: 1fr 1fr; }
  .ap-mb { grid-template-columns: 1fr; }
}
</style>
